import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { zpanelApi } from '../../utils/zpanelApi';

const statusLabels = { active: 'Aktif', suspended: 'Askıda', cancelled: 'İptal' };
const planLabels = { free: 'Ücretsiz', basic: 'Temel', premium: 'Premium' };

const OrganizationsPage = () => {
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await zpanelApi.listOrganizations({ page, limit: 20, status, search });
            setData(res.data || []);
            setPagination(res.pagination || {});
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, status]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchData();
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-white">Organizasyonlar</h1>
                <Link
                    to="/zpanel/organizations/new"
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium"
                >
                    + Yeni Organizasyon
                </Link>
            </div>

            <div className="flex gap-4 mb-6 flex-wrap">
                <form onSubmit={handleSearch} className="flex gap-2">
                    <input
                        type="text"
                        placeholder="Ara (ad, slug...)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 w-48"
                    />
                    <button type="submit" className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg">
                        Ara
                    </button>
                </form>
                <select
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                >
                    <option value="">Tüm Durumlar</option>
                    {Object.entries(statusLabels).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent"></div>
                </div>
            ) : (
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-slate-700/50">
                            <tr>
                                <th className="text-left px-4 py-3 text-slate-300 font-medium">Ad</th>
                                <th className="text-left px-4 py-3 text-slate-300 font-medium">Slug</th>
                                <th className="text-left px-4 py-3 text-slate-300 font-medium">Plan</th>
                                <th className="text-left px-4 py-3 text-slate-300 font-medium">Durum</th>
                                <th className="text-left px-4 py-3 text-slate-300 font-medium">Üye</th>
                                <th className="text-left px-4 py-3 text-slate-300 font-medium">Bitiş</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((org) => (
                                <tr key={org.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                                    <td className="px-4 py-3 text-white font-medium">{org.name}</td>
                                    <td className="px-4 py-3">
                                        <code className="text-amber-400 bg-slate-900 px-2 py-0.5 rounded">{org.slug}</code>
                                    </td>
                                    <td className="px-4 py-3 text-slate-300">{planLabels[org.plan_type] || org.plan_type}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded text-xs ${
                                            org.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                            org.status === 'suspended' ? 'bg-yellow-500/20 text-yellow-400' :
                                            'bg-red-500/20 text-red-400'
                                        }`}>
                                            {statusLabels[org.status] || org.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-400">{org.member_count || 0}</td>
                                    <td className="px-4 py-3 text-slate-400 text-sm">
                                        {org.expires_at ? new Date(org.expires_at).toLocaleDateString('tr-TR') : '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <Link
                                            to={`/zpanel/organizations/${org.id}`}
                                            className="text-amber-400 hover:text-amber-300 text-sm font-medium"
                                        >
                                            Düzenle →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.length === 0 && (
                        <div className="text-center py-12 text-slate-500">Organizasyon bulunamadı</div>
                    )}
                </div>
            )}

            {pagination.totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="px-3 py-1 bg-slate-700 rounded disabled:opacity-50 text-white"
                    >
                        Önceki
                    </button>
                    <span className="px-3 py-1 text-slate-400">
                        {page} / {pagination.totalPages}
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                        disabled={page >= pagination.totalPages}
                        className="px-3 py-1 bg-slate-700 rounded disabled:opacity-50 text-white"
                    >
                        Sonraki
                    </button>
                </div>
            )}
        </div>
    );
};

export default OrganizationsPage;
